import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faChartLine,
  faCalculator,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import RawFallback from "../../Shared/RawFallback/RawFallback";
import styles from "./CSVStatisticalAnalysis.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const escapeRegex = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isNumericVal = (v) => {
  if (v === "" || v == null) return false;
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return !isNaN(n);
};

const computeStatsFromData = (rows, columns, colName) => {
  const colIdx = columns.indexOf(colName);
  if (colIdx < 0) return null;
  const vals = rows.map((r) => (Array.isArray(r) ? r[colIdx] : r[colName])).map((v) => String(v ?? ""));
  if (!vals.every((v) => v === "" || isNumericVal(v))) return null;
  const nums = vals.map((v) => parseFloat(v.replace(/[^0-9.-]/g, "")) || 0).filter((n) => !isNaN(n));
  if (nums.length < 2) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const sum = nums.reduce((a, b) => a + b, 0);
  const mean = sum / nums.length;
  const variance = nums.reduce((a, n) => a + (n - mean) ** 2, 0) / nums.length;
  const stdDev = Math.sqrt(variance);
  const q1Idx = Math.floor(sorted.length * 0.25);
  const medianIdx = Math.floor(sorted.length * 0.5);
  const q3Idx = Math.floor(sorted.length * 0.75);
  return {
    Min: sorted[0],
    Max: sorted[sorted.length - 1],
    Mean: mean,
    "Std Dev": stdDev,
    Median: sorted[medianIdx],
    "Q1 (25th Percentile)": sorted[q1Idx],
    "Q3 (75th Percentile)": sorted[q3Idx],
  };
};

const CSVStatisticalAnalysis = ({ data, rawText, analysisData }) => {
  const sourceColumns = analysisData?.columns || [];
  const sourceData = analysisData?.data || [];

  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>No statistical analysis data available</p>
        </div>
      </div>
    );
  }

  const extractStatisticalData = () => {
    const stats = {
      columns: [],
      summaryTable: null,
      distributionAnalysis: [],
      comparativeStats: [],
    };

    if (!rawText) return stats;

    const text = rawText;
    
    const extractFromSection = (sectionName, content) => {
      if (sectionName.includes("statistical summary")) {
        const columnNames = sourceColumns;
        const statNames = ['Mean', 'Median', 'Mode', 'Std Dev', 'Variance', 'Min', 'Max', 'Range', 
                          'Q1 (25th Percentile)', 'Q2 (50th Percentile)', 'Q3 (75th Percentile)', 
                          'IQR', 'Skewness', 'Distribution Shape'];
        
        columnNames.forEach(colName => {
          if (!stats.columns.find(c => c.name === colName)) {
            stats.columns.push({ name: colName, stats: {} });
          }
        });

        statNames.forEach(statName => {
          const escapedStat = statName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`${escapedStat}:\\s*([^]+?)(?=(?:Mean|Median|Mode|Std Dev|Variance|Min|Max|Range|Q1|Q2|Q3|IQR|Skewness|Distribution Shape):|SECTION:|$)`, 'i');
          const match = content.match(regex);
          if (match) {
            const values = match[1].trim();
            columnNames.forEach((colName) => {
              const colRegex = new RegExp(`${escapeRegex(colName)}:\\s*([^,]+)`, 'i');
              const colMatch = values.match(colRegex);
              if (colMatch) {
                const column = stats.columns.find(c => c.name === colName);
                if (column) {
                  column.stats[statName] = colMatch[1].trim();
                }
              }
            });
          }
        });
      }

      if (sectionName.includes("column statistics")) {
        const columnNames = sourceColumns;
        columnNames.forEach(colName => {
          if (!stats.columns.find(c => c.name === colName)) {
            stats.columns.push({ name: colName, stats: {} });
          }
          
          const column = stats.columns.find(c => c.name === colName);
          if (column) {
            const columnRegex = new RegExp(`${escapeRegex(colName)}:\\s*- ([\\s\\S]+?)(?=[A-Z][a-zA-Z0-9_/]+:|SECTION:|$)`, 'is');
            const columnMatch = content.match(columnRegex);
            
            if (columnMatch) {
              const columnContent = columnMatch[1];
              const statRegex = /- ([^:]+):\s*([^\n-]+)/g;
              let statMatch;
              while ((statMatch = statRegex.exec(columnContent)) !== null) {
                const statKey = statMatch[1].trim();
                const statValue = statMatch[2].trim();
                if (statKey && statValue) {
                  column.stats[statKey] = statValue;
                }
              }
            }
          }
        });
      }

      if (sectionName.includes("distribution analysis")) {
        const bulletRegex = /- ([^\n]+)/g;
        let bulletMatch;
        while ((bulletMatch = bulletRegex.exec(content)) !== null) {
          stats.distributionAnalysis.push({
            type: 'bullet',
            text: bulletMatch[1].trim()
          });
        }
      }

      if (sectionName.includes("comparative statistics")) {
        const bulletRegex = /- ([^\n]+)/g;
        let bulletMatch;
        while ((bulletMatch = bulletRegex.exec(content)) !== null) {
          stats.comparativeStats.push({
            type: 'bullet',
            text: bulletMatch[1].trim()
          });
        }
      }
    };

    const sections = text.split(/(?=SECTION:)/i);
    sections.forEach(section => {
      if (section.trim()) {
        const sectionMatch = section.match(/SECTION:\s*([A-Z][^A-Z]+?)(?=[A-Z][^:]+:|SECTION:|$)/i);
        if (sectionMatch) {
          let sectionName = sectionMatch[1].trim();
          const knownSections = ['Statistical Summary', 'Column Statistics', 'Distribution Analysis', 'Comparative Statistics'];
          for (const known of knownSections) {
            if (sectionName.startsWith(known)) {
              sectionName = known;
              break;
            }
          }
          const content = section.substring(sectionMatch.index + sectionMatch[0].length).trim();
          extractFromSection(sectionName, content);
        }
      }
    });

    if (data.sections) {
      data.sections.forEach((section) => {
        let sectionName = section.name?.toLowerCase() || "";
        
        const knownSections = ['statistical summary', 'column statistics', 'distribution analysis', 'comparative statistics'];
        for (const known of knownSections) {
          if (sectionName.startsWith(known) || sectionName.includes(known)) {
            sectionName = known;
            break;
          }
        }

        if (sectionName.includes("statistical summary")) {
          const columnNames = sourceColumns;
          const statNames = ['Mean', 'Median', 'Mode', 'Std Dev', 'Variance', 'Min', 'Max', 'Range', 
                            'Q1 (25th Percentile)', 'Q2 (50th Percentile)', 'Q3 (75th Percentile)', 
                            'IQR', 'Skewness', 'Distribution Shape'];
          
          columnNames.forEach(colName => {
            if (!stats.columns.find(c => c.name === colName)) {
              stats.columns.push({ name: colName, stats: {} });
            }
          });

          let contentToParse = '';
          if (section.content && section.content.length > 0) {
            contentToParse = section.content.map(item => item.text || item.value || '').join(' ');
          }
          if (rawText) {
            const summarySection = rawText.match(/SECTION:\s*Statistical Summary([^]+?)(?=SECTION:|$)/is);
            if (summarySection) {
              contentToParse = summarySection[1] + ' ' + contentToParse;
            }
          }

          if (contentToParse) {
            statNames.forEach(statName => {
              const escapedStat = statName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const nextStats = statNames.filter(s => s !== statName).map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
              const regex = new RegExp(`${escapedStat}:\\s*([^]+?)(?=(?:${nextStats}):|SECTION:|$)`, 'i');
              const match = contentToParse.match(regex);
              if (match) {
                const values = match[1].trim();
                columnNames.forEach((colName) => {
                  const colRegex = new RegExp(`${escapeRegex(colName)}:\\s*([^,]+)`, 'i');
                  const colMatch = values.match(colRegex);
                  if (colMatch) {
                    const column = stats.columns.find(c => c.name === colName);
                    if (column) {
                      column.stats[statName] = colMatch[1].trim();
                    }
                  }
                });
              }
            });
          }
        }

        if (sectionName.includes("column statistics")) {
          const columnNames = sourceColumns;
          columnNames.forEach(colName => {
            if (!stats.columns.find(c => c.name === colName)) {
              stats.columns.push({ name: colName, stats: {} });
            }
            
            const column = stats.columns.find(c => c.name === colName);
            if (column) {
              let contentToParse = '';
              if (section.content && section.content.length > 0) {
                contentToParse = section.content.map(item => item.text || item.value || '').join(' ');
              }
              if (rawText) {
                const columnRegex = new RegExp(`${escapeRegex(colName)}:\\s*- ([\\s\\S]+?)(?=[A-Z][a-zA-Z0-9_/]+:|SECTION:|$)`, 'is');
                const columnMatch = rawText.match(columnRegex);
                if (columnMatch) {
                  contentToParse = columnMatch[1] + ' ' + contentToParse;
                }
              }
              
              if (contentToParse) {
                const statRegex = /- ([^:]+):\s*([^\n-]+)/g;
                let statMatch;
                while ((statMatch = statRegex.exec(contentToParse)) !== null) {
                  const statKey = statMatch[1].trim();
                  const statValue = statMatch[2].trim();
                  if (statKey && statValue && statValue.length < 200) {
                    if (!column.stats[statKey]) {
                      column.stats[statKey] = statValue;
                    }
                  }
                }
              }
            }
          });
        }

        if (sectionName.includes("distribution analysis")) {
          if (rawText) {
            const distSection = rawText.match(/SECTION:\s*Distribution Analysis([^]+?)(?=SECTION:|$)/is);
            if (distSection) {
              const bulletRegex = /- ([^\n]+)/g;
              let bulletMatch;
              while ((bulletMatch = bulletRegex.exec(distSection[1])) !== null) {
                stats.distributionAnalysis.push({
                  type: 'bullet',
                  text: bulletMatch[1].trim()
                });
              }
            }
          }
          if (section.content) {
            section.content.forEach(item => {
              if (item.type === 'bullet' || item.type === 'text') {
                stats.distributionAnalysis.push({
                  type: 'bullet',
                  text: item.text || item.value || ''
                });
              }
            });
          }
        }

        if (sectionName.includes("comparative statistics")) {
          if (rawText) {
            const compSection = rawText.match(/SECTION:\s*Comparative Statistics([^]+?)(?=SECTION:|$)/is);
            if (compSection) {
              const bulletRegex = /- ([^\n]+)/g;
              let bulletMatch;
              while ((bulletMatch = bulletRegex.exec(compSection[1])) !== null) {
                stats.comparativeStats.push({
                  type: 'bullet',
                  text: bulletMatch[1].trim()
                });
              }
            }
          }
          if (section.content) {
            section.content.forEach(item => {
              if (item.type === 'bullet' || item.type === 'text') {
                stats.comparativeStats.push({
                  type: 'bullet',
                  text: item.text || item.value || ''
                });
              }
            });
          }
        }
      });
    }

    stats.columns.forEach((col) => {
      const computed = computeStatsFromData(sourceData, sourceColumns, col.name);
      if (computed) {
        Object.entries(computed).forEach(([k, v]) => {
          if (!col.stats[k] || col.stats[k] === "N/A") {
            col.stats[k] = typeof v === "number" ? String(v) : v;
          }
        });
      }
    });

    return stats;
  };

  const statisticalData = extractStatisticalData();

  const formatNumber = (value) => {
    if (!value || value === "N/A") return "N/A";
    const num = parseFloat(value.toString().replace(/[^0-9.-]/g, ""));
    if (isNaN(num)) return value;
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const hasNumericStats = (col) => {
    const s = col.stats || {};
    return !!(s.Mean ?? s["Mean"] ?? s.Median ?? s["Median"] ?? s.Min ?? s["Min"] ?? s.Max ?? s["Max"] ?? s["Std Dev"] ?? s["Standard Deviation"]);
  };

  const importantKeywords = /mAP|map|precision|recall|accuracy|box_loss|seg_loss|cls_loss|val\//i;
  const numericColumns = statisticalData.columns
    .filter(hasNumericStats)
    .sort((a, b) => {
      const aImp = importantKeywords.test(a.name);
      const bImp = importantKeywords.test(b.name);
      if (aImp !== bImp) return aImp ? -1 : 1;
      return 0;
    });

  const createHistogramData = (column) => {
    const stats = column.stats || {};
    const min = parseFloat(stats.Min || stats["Min"] || stats["min"]);
    const max = parseFloat(stats.Max || stats["Max"] || stats["max"]);
    if (isNaN(min) || isNaN(max) || min === max) return null;

    const colIdx = sourceColumns.indexOf(column.name);
    const values = sourceData
      .map((r) => (Array.isArray(r) ? r[colIdx] : r[column.name]))
      .map((v) => parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")))
      .filter((n) => !isNaN(n));

    const range = max - min || 1;
    const bins = Math.min(10, Math.max(5, Math.ceil(values.length / 10)));
    const binWidth = range / bins;
    const counts = new Array(bins).fill(0);
    values.forEach((v) => {
      let idx = Math.min(Math.floor((v - min) / binWidth), bins - 1);
      if (idx < 0) idx = 0;
      counts[idx]++;
    });

    const labels = [];
    for (let i = 0; i < bins; i++) {
      const binStart = min + i * binWidth;
      const binEnd = binStart + binWidth;
      labels.push(`${formatNumber(binStart)}-${formatNumber(binEnd)}`);
    }
    return { labels, data: counts };
  };

  const createBoxplotData = (column) => {
    const stats = column.stats || {};
    const min = parseFloat(stats.Min || stats["Min"] || stats["min"] || 0);
    const q1 = parseFloat(stats["Q1 (25th Percentile)"] || stats["25th Percentile"] || stats["25th percentile"] || 0);
    const median = parseFloat(stats.Median || stats["Median"] || stats["median"] || 0);
    const q3 = parseFloat(stats["Q3 (75th Percentile)"] || stats["75th Percentile"] || stats["75th percentile"] || 0);
    const max = parseFloat(stats.Max || stats["Max"] || stats["max"] || 100);
    
    if (isNaN(min) || isNaN(q1) || isNaN(median) || isNaN(q3) || isNaN(max)) {
      return null;
    }

    return { min, q1, median, q3, max };
  };

  const renderBoxplot = (column) => {
    const boxData = createBoxplotData(column);
    if (!boxData) return null;

    const { min, q1, median, q3, max } = boxData;
    const range = max - min;
    const width = 200;
    const height = 120;
    const padding = 20;
    
    const scale = (value) => padding + ((value - min) / range) * (width - 2 * padding);
    
    const q1Pos = scale(q1);
    const medianPos = scale(median);
    const q3Pos = scale(q3);
    const boxWidth = q3Pos - q1Pos;
    const boxHeight = 40;
    const boxY = (height - boxHeight) / 2;

    return (
      <svg width={width} height={height} className={styles.boxplotSvg}>
        <line x1={scale(min)} y1={boxY + boxHeight / 2} x2={scale(q1)} y2={boxY + boxHeight / 2} stroke="var(--primary-color)" strokeWidth="2" />
        <rect x={q1Pos} y={boxY} width={boxWidth} height={boxHeight} fill="rgba(16, 185, 129, 0.2)" stroke="var(--primary-color)" strokeWidth="2" />
        <line x1={medianPos} y1={boxY} x2={medianPos} y2={boxY + boxHeight} stroke="var(--primary-color)" strokeWidth="2" />
        <line x1={scale(q3)} y1={boxY + boxHeight / 2} x2={scale(max)} y2={boxY + boxHeight / 2} stroke="var(--primary-color)" strokeWidth="2" />
        <line x1={scale(min)} y1={boxY + boxHeight / 2 - 5} x2={scale(min)} y2={boxY + boxHeight / 2 + 5} stroke="var(--primary-color)" strokeWidth="2" />
        <line x1={scale(max)} y1={boxY + boxHeight / 2 - 5} x2={scale(max)} y2={boxY + boxHeight / 2 + 5} stroke="var(--primary-color)" strokeWidth="2" />
        <text x={scale(min)} y={height - 5} textAnchor="middle" fill="var(--text-secondary)" fontSize="10">{formatNumber(min)}</text>
        <text x={scale(median)} y={boxY - 5} textAnchor="middle" fill="var(--primary-color)" fontSize="10" fontWeight="bold">{formatNumber(median)}</text>
        <text x={scale(max)} y={height - 5} textAnchor="middle" fill="var(--text-secondary)" fontSize="10">{formatNumber(max)}</text>
      </svg>
    );
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        titleColor: "#10b981",
        bodyColor: "#e5e7eb",
        borderColor: "#10b981",
        borderWidth: 1,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "rgba(229, 231, 235, 0.1)" },
        ticks: { color: "#9ca3af", font: { size: 11 } },
      },
      x: {
        grid: { display: false },
        ticks: { color: "#9ca3af", font: { size: 10 }, maxRotation: 45, minRotation: 45 },
      },
    },
  };

  const renderColumnCard = (column) => {
    const stats = column.stats || {};
    const statItems = [
      { label: "Mean", value: stats.Mean || stats["Mean"] || stats["mean"] },
      { label: "Median", value: stats.Median || stats["Median"] || stats["median"] },
      { label: "Std Dev", value: stats["Std Dev"] || stats["Standard Deviation"] || stats["standard deviation"] },
      { label: "Min", value: stats.Min || stats["Min"] || stats["min"] },
      { label: "Max", value: stats.Max || stats["Max"] || stats["max"] },
      { label: "Q1", value: stats["Q1 (25th Percentile)"] || stats["25th Percentile"] || stats["25th percentile"] },
      { label: "Q3", value: stats["Q3 (75th Percentile)"] || stats["75th Percentile"] || stats["75th percentile"] },
    ].filter((item) => item.value);

    const histogramData = createHistogramData(column);
    const boxData = createBoxplotData(column);
    const hasCharts = (histogramData && histogramData.data.some((v) => v > 0)) || boxData;

    return (
      <div key={column.name} className={styles.columnCard}>
        <div className={styles.columnCardHeader}>
          <h3 className={styles.columnCardTitle}>{column.name}</h3>
          {statItems.length > 0 && (
            <div className={styles.statsRow}>
              {statItems.map((item, idx) => (
                <span key={idx} className={styles.statsChip}>
                  {item.label}: <strong>{formatNumber(item.value)}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
        {hasCharts && (
          <div className={styles.columnCardCharts}>
            {histogramData && histogramData.data.some((v) => v > 0) && (
              <div className={styles.histogramWrap}>
                <Bar
                  data={{
                    labels: histogramData.labels,
                    datasets: [
                      {
                        data: histogramData.data,
                        backgroundColor: "rgba(16, 185, 129, 0.6)",
                        borderColor: "#10b981",
                        borderWidth: 1,
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={chartOptions}
                />
              </div>
            )}
            {boxData && (
              <div className={styles.boxplotWrap}>
                {renderBoxplot(column)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDistributionInsights = () => {
    if (!statisticalData.distributionAnalysis || statisticalData.distributionAnalysis.length === 0) {
      return null;
    }

    return (
      <div className={styles.insightsSection}>
        <h3 className={styles.sectionTitle}>Distribution Summary Insights</h3>
        <div className={styles.insightsCard}>
          <ul className={styles.insightsList}>
            {statisticalData.distributionAnalysis.map((item, idx) => {
              if (item.type === "bullet" && item.text) {
                return (
                  <li key={idx} className={styles.insightItem}>
                    <span className={styles.bulletPoint}>•</span>
                    <span>{item.text}</span>
                  </li>
                );
              } else if (item.type === "text" && item.text) {
                return (
                  <li key={idx} className={styles.insightItem}>
                    <span className={styles.bulletPoint}>•</span>
                    <span>{item.text}</span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>
      </div>
    );
  };

  const renderComparativeStatistics = () => {
    if (!statisticalData.comparativeStats || statisticalData.comparativeStats.length === 0) {
      return null;
    }

    return (
      <div className={styles.insightsSection}>
        <h3 className={styles.sectionTitle}>Comparative Statistics</h3>
        <div className={styles.insightsCard}>
          <ul className={styles.insightsList}>
            {statisticalData.comparativeStats.map((item, idx) => {
              if (item.type === "bullet" && item.text) {
                return (
                  <li key={idx} className={styles.insightItem}>
                    <span className={styles.bulletPoint}>•</span>
                    <span>{item.text}</span>
                  </li>
                );
              } else if (item.type === "text" && item.text) {
                return (
                  <li key={idx} className={styles.insightItem}>
                    <span className={styles.bulletPoint}>•</span>
                    <span>{item.text}</span>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon icon={faCalculator} />
        </div>
        <div>
          <h2 className={styles.title}>Statistical Analysis</h2>
          <p className={styles.subtitle}>Means, medians, distributions</p>
        </div>
      </div>

      <div className={styles.columnsSection}>
        <h3 className={styles.sectionTitle}>Numeric Columns</h3>
        {numericColumns.length > 0 ? (
          <div className={styles.columnsList}>
            {numericColumns.slice(0, 8).map((column) => renderColumnCard(column))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No numeric columns found. Statistics and distributions are shown only for columns with numeric data.</p>
            {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
          </div>
        )}
      </div>
      {renderDistributionInsights()}
      {renderComparativeStatistics()}
    </div>
  );
};

export default CSVStatisticalAnalysis;

